import { Component, ElementRef, OnInit, ViewChild, inject } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ActivatedRoute, NavigationEnd, Params, Router, UrlSegment, UrlTree } from '@angular/router';
import { EprocUser, UserService } from '@lib/user';
import { PlatformService, View } from '@lib/core';
import { distinctUntilChanged, filter, map, startWith, takeUntil } from 'rxjs/operators';
import { BarcodeScanner, BarcodeScanResult } from '@awesome-cordova-plugins/barcode-scanner/ngx';
import { EprocBarcodeScannerOptions, Logger, SubscriptionHelper } from '@lib/util';
import { QrCodeService } from '@lib/qr-code';
import { SearchFieldStore } from './search-field.store';

enum SearchFieldKey {
    ARROW_DOWN = 'ArrowDown',
    ARROW_UP = 'ArrowUp',
    ENTER = 'Enter',
    ESCAPE = 'Escape',
    TAB = 'Tab',
    DELETE = 'Delete',
}

@Component({
    selector: 'search',
    templateUrl: './search.component.html',
    styleUrls: ['./search.component.css'],
    standalone: false,
    providers: [SearchFieldStore],
})
export class SearchComponent extends SubscriptionHelper implements OnInit {
    private readonly userService = inject(UserService);
    private readonly router = inject(Router);
    private readonly route = inject(ActivatedRoute);
    private readonly barcodeScanner = inject(BarcodeScanner);
    private readonly platformService = inject(PlatformService);
    private readonly qrCodeService = inject(QrCodeService);
    public readonly searchFieldStore = inject(SearchFieldStore);

    @ViewChild('searchEl') private readonly searchEl!: ElementRef<HTMLInputElement>;

    public searchControl?: FormControl<string>;
    public readonly spinnerName: string = 'searchSpinner';
    public view: View;
    public searchOpen = false;
    public isNative = false;

    constructor() {
        super();
        this.isNative = this.platformService.isNative;
    }

    public barcodeScan(): void {
        this.barcodeScanner
            .scan(new EprocBarcodeScannerOptions())
            .then((result: BarcodeScanResult) => {
                Logger.info(
                    'BARCODE_SCAN',
                    `Scanned code. Cancelled: ${result.cancelled}; Result text: ${result.text}; Format: ${result.format}`,
                );
                if (!result.cancelled && QrCodeService.isQrCodeLink(result.text)) {
                    this.qrCodeService.processQrCodeLink(result.text);
                } else if (!result.cancelled) {
                    this.searchControl.setValue(result.text);
                    this.search();
                }
            })
            .catch((error) => console.error('BARCODE_SCANNER_ERROR', error));
    }

    public reset(): void {
        this.searchControl.setValue('');
    }

    public closeSearchResults(): void {
        this.searchOpen = false;
        this.searchFieldStore.closeList();
    }

    public onSearchKeydown(event: KeyboardEvent): void {
        const activeQuery: string | null = this.searchFieldStore.recentListVisible()
            ? this.searchFieldStore.activeRecentQuery()
            : null;

        switch (event.key) {
            case SearchFieldKey.ARROW_DOWN:
            case SearchFieldKey.ARROW_UP:
                if (this.searchFieldStore.recentSearchesEnabled()) {
                    event.preventDefault();
                    this.highlightRecentQuery(event.key === SearchFieldKey.ARROW_DOWN ? 1 : -1);
                }
                break;
            case SearchFieldKey.ENTER:
                if (activeQuery !== null) {
                    event.preventDefault();
                    this.search();
                }
                break;
            case SearchFieldKey.DELETE:
                if (activeQuery !== null) {
                    event.preventDefault();
                    this.searchFieldStore.removeRecentQuery(activeQuery);
                    // the highlighted entry was shown in the field, restore the text it had before
                    this.searchFieldStore.restoreTextBeforeHighlight();
                    this.searchControl.setValue(this.searchFieldStore.query(), { emitEvent: false });
                }
                break;
            case SearchFieldKey.ESCAPE:
                // keeps type="search" from clearing the field, which would reopen the list via input
                event.preventDefault();
                this.searchFieldStore.closeList();
                break;
            case SearchFieldKey.TAB:
                this.searchFieldStore.closeList();
                break;
        }
    }

    /**
     * Takes the entry as text of the field without searching; the user starts the search himself.
     */
    public selectRecentQuery(query: string): void {
        this.searchControl.setValue(query, { emitEvent: false });
        this.searchFieldStore.takeQuery(query);
        const field: HTMLInputElement = this.searchEl.nativeElement;
        field.focus();
        field.setSelectionRange(query.length, query.length);
        this.searchFieldStore.closeList();
    }

    private highlightRecentQuery(offset: number): void {
        this.searchFieldStore.moveActive(offset);
        if (this.searchFieldStore.activeRecentQuery() !== null) {
            this.searchControl.setValue(this.searchFieldStore.query(), { emitEvent: false });
        }
    }

    public removeRecentQuery(event: Event, query: string): void {
        event.stopPropagation();
        this.searchFieldStore.removeRecentQuery(query);
    }

    async ngOnInit(): Promise<void> {
        this.view = await this.userService.getViewFromUserType();
        const params: Params = this.route.snapshot.queryParams;
        this.searchControl = new FormControl<string>(params && params.query ? params.query : '');
        this.initQueryFromRoute();
        await this.initSearchFieldStore();
    }

    public async search(): Promise<void> {
        const searchString: string = this.searchControl.value.trim();

        if (this.searchEl) {
            requestAnimationFrame(() => this.searchEl.nativeElement.blur());
        } else {
            (document.activeElement as HTMLElement)?.blur();
        }

        if (this.view && searchString) {
            switch (this.view) {
                case View.CLIENT:
                    this.searchFieldStore.closeList();
                    this.searchFieldStore.takeQuery(this.searchControl.value);
                    this.searchFieldStore.addRecentQuery(searchString);
                    await this.router.navigate([View.CLIENT, 'search'], { queryParams: { query: searchString } });
                    break;
            }
        }
    }

    private async initSearchFieldStore(): Promise<void> {
        this.searchFieldStore.takeQuery(this.searchControl.value);
        this.searchControl.valueChanges
            .pipe(takeUntil(this.onDestroy$))
            .subscribe((value: string) => this.searchFieldStore.setQuery(value));

        if (this.view === View.CLIENT) {
            const user: EprocUser = await this.userService.getMyself();
            await this.searchFieldStore.enableRecentSearches(user.id);
        }
    }

    /**
     * Keeps the query of the client result list in the field, also after reload, back navigation or
     * an opened search link. Leaving the result list keeps the field as it is.
     */
    private initQueryFromRoute(): void {
        if (this.view !== View.CLIENT) {
            return;
        }
        this.router.events
            .pipe(
                filter((event): event is NavigationEnd => event instanceof NavigationEnd),
                map((event: NavigationEnd) => event.urlAfterRedirects),
                startWith(this.router.url),
                map((url: string) => this.resultListQueryOf(url)),
                distinctUntilChanged(),
                filter((query: string | null): query is string => query !== null),
                takeUntil(this.onDestroy$),
            )
            .subscribe((query: string) => {
                this.searchControl.setValue(query, { emitEvent: false });
                this.searchFieldStore.takeQuery(query);
            });
    }

    /** The query of the client result list lives in the address; every other page yields null. */
    private resultListQueryOf(url: string): string | null {
        const tree: UrlTree = this.router.parseUrl(url);
        const path: string = (tree.root.children['primary']?.segments ?? [])
            .map((segment: UrlSegment) => segment.path)
            .join('/');
        if (path !== `${View.CLIENT}/search`) {
            return null;
        }
        const query: unknown = tree.queryParams['query'];
        return typeof query === 'string' ? query : null;
    }
}
